import { c as _c } from "react/compiler-runtime";
import DropPfp from "@/components/drops/create/utils/DropPfp";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { formatStatFloor } from "@/helpers/Helpers";
import { shortenAddress } from "@/helpers/address.helpers";
import { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import Link from "next/link";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
export type ParticipationIdentityProfileCardVariant = "default" | "chat";
interface ParticipationIdentityProfileCardProps {
  readonly profile: ApiProfileMin;
  readonly contextId?: string | number | undefined;
  readonly variant?: ParticipationIdentityProfileCardVariant | undefined;
}
interface IdentityStatLinkProps {
  readonly href: string;
  readonly label: string;
  readonly value: number;
  readonly rate?: number | undefined;
  readonly compact?: boolean | undefined;
}
function IdentityStatLink(t0) {
  const $ = _c(16);
  const {
    href,
    label,
    value,
    rate,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const valueClass = compact ? "tw-text-[13px] tw-font-semibold tw-leading-none tw-text-iron-200" : "tw-text-sm tw-font-semibold tw-text-iron-200";
  const labelClass = compact ? "tw-text-[13px] tw-font-medium tw-leading-none tw-tracking-wide tw-text-iron-500 tw-uppercase" : "tw-text-sm tw-font-medium tw-tracking-wide tw-text-iron-500 tw-uppercase";
  const rateClass = compact ? "tw-text-[13px] tw-font-semibold tw-leading-none tw-text-emerald-500" : "tw-text-xs tw-font-semibold tw-leading-4 tw-text-emerald-500";
  let t2;
  if ($[0] !== value) {
    t2 = formatStatFloor(value);
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2 || $[3] !== valueClass) {
    t3 = <span className={valueClass}>{t2}</span>;
    $[2] = t2;
    $[3] = valueClass;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label || $[6] !== labelClass) {
    t4 = <span className={labelClass}>{label}</span>;
    $[5] = label;
    $[6] = labelClass;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== rate || $[9] !== rateClass) {
    t5 = typeof rate === "number" && rate > 0 && <>{" "}<span className={rateClass}>+{formatStatFloor(rate)}</span></>;
    $[8] = rate;
    $[9] = rateClass;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== href || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <Link href={href} prefetch={false} onClick={_temp} className="tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-underline">{t3}{" "}{t4}{t5}</Link>;
    $[11] = href;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(event) {
  return event.stopPropagation();
}
export default function ParticipationIdentityProfileCard(t0) {
  const $ = _c(71);
  const {
    profile,
    contextId,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const isChat = variant === "chat";
  const profileLabel = profile.handle ?? profile.primary_address;
  let t2;
  if ($[0] !== profileLabel) {
    t2 = profileLabel.toLowerCase();
    $[0] = profileLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const routeIdentity = encodeURIComponent(t2);
  const rootHref = `/${routeIdentity}`;
  let t3;
  if ($[2] !== profile.handle || $[3] !== profile.primary_address) {
    t3 = !!profile.handle && profile.primary_address.toLowerCase() !== profile.handle.toLowerCase();
    $[2] = profile.handle;
    $[3] = profile.primary_address;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const shouldShowAddress = t3;
  let t4;
  if ($[5] !== profile.primary_address || $[6] !== shouldShowAddress) {
    t4 = shouldShowAddress ? shortenAddress(profile.primary_address) : null;
    $[5] = profile.primary_address;
    $[6] = shouldShowAddress;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const displayAddress = t4;
  const containerClassName = isChat ? "tw-mt-4 tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800/80 tw-bg-iron-900/60 tw-px-3 tw-py-2.5" : "tw-mt-3 tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800/80 tw-bg-iron-900/60 tw-p-4";
  const headerRowClassName = isChat ? "tw-flex tw-min-w-0 tw-items-center tw-gap-2.5" : "tw-flex tw-min-w-0 tw-items-start tw-gap-3";
  const metaColumnClassName = isChat ? "tw-flex tw-min-w-0 tw-flex-col tw-justify-center tw-gap-y-0.5" : "tw-flex tw-min-w-0 tw-flex-col tw-gap-y-1";
  const levelSize = isChat ? UserCICAndLevelSize.COMPACT : UserCICAndLevelSize.SMALL;
  const badgeContainerClassName = isChat ? "tw-inline-flex tw-items-center tw-gap-x-1" : undefined;
  const statsClassName = isChat ? "tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-1.5" : "tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-2";
  const t5 = `View ${profileLabel}'s profile`;
  const t6 = isChat ? ProfileBadgeSize.COMPACT : ProfileBadgeSize.MEDIUM;
  let t7;
  if ($[8] !== profile.pfp || $[9] !== t6) {
    t7 = <DropPfp pfpUrl={profile.pfp} profileSize={t6} />;
    $[8] = profile.pfp;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== rootHref || $[12] !== t5 || $[13] !== t7) {
    t8 = <Link href={rootHref} prefetch={false} onClick={_temp2} className="tw-flex-shrink-0 tw-no-underline" aria-label={t5}>{t7}</Link>;
    $[11] = rootHref;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== profileLabel) {
    t9 = <span className="tw-text-sm tw-font-semibold tw-leading-none">{profileLabel}</span>;
    $[15] = profileLabel;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== rootHref || $[18] !== t9) {
    t10 = <Link href={rootHref} prefetch={false} onClick={_temp3} className="tw-mb-0 tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{t9}</Link>;
    $[17] = rootHref;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== levelSize || $[21] !== profile.level) {
    t11 = <UserCICAndLevel level={profile.level} size={levelSize} />;
    $[20] = levelSize;
    $[21] = profile.level;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = `identity-profile-card-${contextId ?? profile.id}`;
  const t13 = isChat ? "compact" : "default";
  let t14;
  if ($[23] !== badgeContainerClassName || $[24] !== profile || $[25] !== t12 || $[26] !== t13) {
    t14 = <DropAuthorBadges profile={profile} tooltipIdPrefix={t12} className={badgeContainerClassName} size={t13} />;
    $[23] = badgeContainerClassName;
    $[24] = profile;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t10}{t11}{t14}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== displayAddress || $[33] !== profile.primary_address) {
    t16 = displayAddress && <p title={profile.primary_address} className="tw-mb-0 tw-text-xs tw-text-white/40">{displayAddress}</p>;
    $[32] = displayAddress;
    $[33] = profile.primary_address;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== metaColumnClassName || $[36] !== t15 || $[37] !== t16) {
    t17 = <div className={metaColumnClassName}>{t15}{t16}</div>;
    $[35] = metaColumnClassName;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== headerRowClassName || $[40] !== t17 || $[41] !== t8) {
    t18 = <div className="tw-flex tw-items-start tw-justify-between tw-gap-3"><div className={headerRowClassName}>{t8}{t17}</div></div>;
    $[39] = headerRowClassName;
    $[40] = t17;
    $[41] = t8;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = `${rootHref}/collected`;
  let t20;
  if ($[43] !== isChat || $[44] !== profile.tdh || $[45] !== profile.tdh_rate || $[46] !== t19) {
    t20 = <IdentityStatLink href={t19} label="TDH" value={profile.tdh} rate={profile.tdh_rate} compact={isChat} />;
    $[43] = isChat;
    $[44] = profile.tdh;
    $[45] = profile.tdh_rate;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = `${rootHref}/xtdh`;
  let t22;
  if ($[48] !== isChat || $[49] !== profile.xtdh || $[50] !== profile.xtdh_rate || $[51] !== t21) {
    t22 = <IdentityStatLink href={t21} label="xTDH" value={profile.xtdh} rate={profile.xtdh_rate} compact={isChat} />;
    $[48] = isChat;
    $[49] = profile.xtdh;
    $[50] = profile.xtdh_rate;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== isChat || $[54] !== profile.cic || $[55] !== rootHref) {
    t23 = <IdentityStatLink href={rootHref} label="NIC" value={profile.cic} compact={isChat} />;
    $[53] = isChat;
    $[54] = profile.cic;
    $[55] = rootHref;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== isChat || $[58] !== profile.rep || $[59] !== rootHref) {
    t24 = <IdentityStatLink href={rootHref} label="Rep" value={profile.rep} compact={isChat} />;
    $[57] = isChat;
    $[58] = profile.rep;
    $[59] = rootHref;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== statsClassName || $[62] !== t20 || $[63] !== t22 || $[64] !== t23 || $[65] !== t24) {
    t25 = <div className={statsClassName}>{t20}{t22}{t23}{t24}</div>;
    $[61] = statsClassName;
    $[62] = t20;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== containerClassName || $[68] !== t18 || $[69] !== t25) {
    t26 = <div className={containerClassName}>{t18}{t25}</div>;
    $[67] = containerClassName;
    $[68] = t18;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  return t26;
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.stopPropagation();
}